import { useState, useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import ContactFormModal from '@/components/sharedComponents/ContactFormModal';


const PriceDisplay = ({ price, billing, isEnterprise }) => {
  if (isEnterprise || typeof price === 'string') {
    return (
      <div className="flex items-center justify-center">
        <span className="text-2xl md:text-3xl text-white leading-none">{price}</span>
      </div>
    );
  }

  const integer = Math.floor(price);
  const cents = String(price.toFixed(2)).split('.')[1];

  return (
    <div className="flex items-start justify-center gap-0.5">
      <span className="text-2xl md:text-3xl text-white leading-none">$</span>
      <span className="text-2xl md:text-3xl text-white leading-none">{integer}</span>
      <sup className="text-xs md:text-sm text-white -mt-1 ml-0.5 self-start">{cents}</sup>
    </div>
  );
};

const PlanCard = ({ planKey, plan, isEnterprise, onCheckout, isCheckingOut, isActive, hasActivePlan, onContactForm }) => {
  const accentColor = isEnterprise ? 'text-[#c9a84c]' : 'text-[#3b82f6]';

  const billingLabel = plan.billing === 'per_permit' ? '/per permit' : plan.billing === 'monthly' ? '/monthly' : null;
  const navigate = useNavigate();
  const handleNewPermit = () => {
    navigate('/citypermit/project/new-chat');
  };


  return (
    // rounded-2xl px-6 pt-6 pb-4 flex flex-col bg-white/[0.06] backdrop-blur-sm border border-white/10 ring-2 ring-blue-500 h-full"
    <div className={cn(`rounded-2xl px-6 pt-6 pb-4 flex flex-col backdrop-blur-sm border border-white/10 h-full ${planKey != 'starter' ? 'bg-[#001322] ' : 'bg-black'} ${isEnterprise ? "ring-2 ring-amber-500 " : ""}`)}>
      {/* Name */}
      <h3 className="text-xl text-white text-center">{plan.name}</h3>

      {/* Price */}
      <div className='mt-6'>
        <PriceDisplay price={plan.price} billing={plan.billing} isEnterprise={isEnterprise} />

        {/* Billing label */}
        <p className="text-center text-sm text-gray-400 mt-1 mb-4 min-h-[20px]">{billingLabel ?? ''}</p>
      </div>
      {/* Description */}
      <p
        className="
        relative py-6 mb-5 min-h-[10px]
        text-center text-sm text-white leading-relaxed

        before:content-[''] before:absolute before:inset-x-0 before:top-0 before:h-px
        after:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-px

        before:bg-[radial-gradient(ellipse_at_center,_rgba(107,107,107,0.8)_0%,_transparent_60%)]
        after:bg-[radial-gradient(ellipse_at_center,_rgba(107,107,107,0.8)_0%,_transparent_60%)]
  "
      >
        {plan.description}
      </p>



      {/* What's Included */}
      <p className={cn('text-lg font-semibold text-subscriptionbutton mt-6 mb-3')}>What&apos;s Included</p>

      <ul className="space-y-2 flex-1 min-h-[160px]">
        {plan.features.map((feature, i) => (
          <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
            {/* <Check className={cn('text-emerald-400 shrink-0 mt-0.5 size-3.5')} />  */}
            <span class="text-subscriptionbutton shrink-0 mt-0.5">✓</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-6">
        {isEnterprise ? (
          <>
            <div className='mt-auto pt-6 border-t border-white/10 flex flex-col'>

              <Button
                className="w-full py-3 rounded-full font-medium transition-colors bg-subscriptionbutton hover:bg-subscriptionbutton/80 text-white flex items-center justify-center gap-2 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onContactForm}
                disabled={hasActivePlan && !isActive}
              >
                Get a Quote
                <span className="inline-flex items-center justify-end w-5 h-5 rounded-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                </span>
              </Button>
              <p className="text-center text-[11px] text-gray-500 mt-2">Flexible contract options</p>
            </div>
          </>
        ) : isActive ? (
          <>
            <div className='mt-auto pt-6 border-t border-white/10 flex flex-col'>

              <Button
                className="w-full bg-green-600 hover:bg-green-600/80 text-white font-semibold rounded-full h-10 text-sm cursor-pointer"
                onClick={() => { navigate('/citypermit/project/new-chat'); }}
              >
                <Check className="w-4 h-4 mr-1.5" />
                Current package
              </Button>
              <p className="text-center text-[11px] text-gray-500 mt-2">Your current active plan.</p>
            </div>
          </>
        ) : planKey === 'starter' ? (
          <>
            <div className="mt-auto pt-6 border-t border-white/10 flex flex-col">

              <Button
                className="w-full py-3 rounded-full font-medium transition-colors bg-subscriptionbutton hover:bg-subscriptionbutton/80 text-white flex items-center justify-center gap-2 shrink-0"
                onClick={() => handleNewPermit()}
                disabled={hasActivePlan}
              >
                Chat With Zermit Now
                <span className="inline-flex items-center justify-end w-5 h-5 rounded-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                </span>
              </Button>
              <p className="text-center text-[11px] text-gray-500 mt-2 invisible">
                Cancel Anytime. No long-term contracts.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className='mt-auto pt-6 border-t border-white/10 flex flex-col'>
              <Button
                className="w-full py-3 rounded-full font-medium transition-colors bg-subscriptionbutton hover:bg-subscriptionbutton/80 text-white flex items-center justify-center gap-2 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isCheckingOut || hasActivePlan}
                onClick={onCheckout}
              >
                {isCheckingOut ? 'Redirecting...' : `Start with ${plan.name}`}
                <span className="inline-flex items-center justify-end w-5 h-5 rounded-sm">
                  {/* <ArrowRight className="w-10 h-3 fill-current text-white" /> */}
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                </span>
              </Button>
              <p className="text-center text-[11px] text-gray-500 mt-2">Cancel Anytime. No long-term contracts.</p>
            </div>
          </>
        )}
      </div>

      <div className='min-h-[18px] mt-1 flex items-center justify-center flex-col'>
        <small className='text-slate-500 text-xs text-center'>
          City-imposed fees not included
        </small>
      </div>
    </div>
  );
};

const SubscriptionPage = ({ protectedRoute = true }) => {
  const [plans, setPlans] = useState(null);
  const [activePlanId, setActivePlanId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [checkingOutKey, setCheckingOutKey] = useState(null);
  const [showContactFormModal, setShowContactFormModal] = useState(false);
  const [isAutoCheckingOut, setIsAutoCheckingOut] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return protectedRoute && params.get('from_zermit') === 'true' && !!params.get('price_id');
  });
  const navigate = useNavigate();
  const location = useLocation();


  const handleCheckout = async (planKey, priceId) => {
    if (!protectedRoute) {
      navigate('/citypermit/subscription');
      return
    }
    setCheckingOutKey(planKey);
    try {
      const currentUrl = window.location.href;
      const { data } = await appAxios.post('/Subscription/v1.0/create-checkout-session', { priceId: priceId, successUrl: currentUrl, cancelUrl: currentUrl });
      window.location.href = data.data.checkout_url;
    } catch (error) {
      if (error.response.data.ui_action === "already_active_subscription_toast") {
        toast.info('You are already subscribed user.');
        return;
      }
      if (error.response.data.ui_action === "show_wait_toast") {
        toast.info('We are setting up your account. Please wait for a moment.');
        return;
      }

      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsAutoCheckingOut(false);
      navigate('/citypermit/subscription');
    } finally {
      setCheckingOutKey(null);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        let url
        if (protectedRoute) {
          url = '/Subscription/v1.0/plans';
        } else {
          url = '/PermitChat/v1.0/plans';
        }
        const { data } = await appAxios.get(url);
        if (data.data.activePlan) {
          setActivePlanId(data.data.activePlan);
        }
        setPlans(data.data.plans);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
        toast.error('Failed to load subscription plans.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    if (!plans) return;

    const params = new URLSearchParams(location.search);
    const priceId = params.get('price_id');
    const from_zermit_bool = params.get('from_zermit') === 'true';

    if (!priceId || !from_zermit_bool) return;

    if (protectedRoute) {
      const planKey = Object.keys(plans).find(key => plans[key].price_id === priceId);
      handleCheckout(planKey, priceId).catch(() => setIsAutoCheckingOut(false));
    } else {
      navigate(`/citypermit/subscription?price_id=${priceId}&from_zermit=true`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans])

  const planOrder = ['starter', 'pro_1', 'pro_2', 'enterprise'];
  const professionalKeys = ['pro_1', 'pro_2'];

  return (
    <div className="flex flex-col min-h-full w-full overflow-y-auto no-scrollbar font-baloo-400 bg-newbackground">
      <div>
        {/* Hero Header */}
        <div className="relative z-10 w-full max-w-7xl mx-auto text-center px-4 py-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight text-center break-words">
            Get Your Permit Submitted Online in 2–3 Minutes
          </h1>
          <p class="text-white/100 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto text-center whitespace-pre-line px-1">
            Pull over-the-counter (OTC) and like-to-like permits online through AI-powered permit automation. Choose a monthly plan or pay per permit no long-term contracts required.
          </p>
        </div>

        {/* Plans Section */}
        <div className="my-4 px-6">
          {isLoading || isAutoCheckingOut ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-7 h-7 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : plans ? (
            <div className="m-2 mx-auto max-w-7xl">
              {/* Plan Cards */}
              <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6">
                {planOrder.map(key => {
                  const plan = plans[key];
                  if (!plan) return null;
                  return (
                    <PlanCard
                      key={key}
                      planKey={key}
                      plan={plan}
                      isHighlighted={professionalKeys.includes(key)}
                      isEnterprise={key === 'enterprise'}
                      onCheckout={() => handleCheckout(key, plan.price_id)}
                      isCheckingOut={checkingOutKey === key}
                      isActive={!!activePlanId && plan.price_id === activePlanId}
                      hasActivePlan={!!activePlanId}
                      onContactForm={() => setShowContactFormModal(true)}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-400 py-20">No plans available at this time.</p>
          )}
        </div>
      </div>
      <ContactFormModal
        isOpen={showContactFormModal}
        onClose={() => setShowContactFormModal(false)}
        inquiryType="enterprise_plan"
      />
    </div>
  );
};

export default SubscriptionPage;
